import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../../hooks/useApi';
import './TaxReportsProgressPanel.css';

/**
 * Компонент для отображения прогресса загрузки WB отчётов
 */
import logger from "../../utils/logger";
import { fmtTime } from '../../utils/ruDates';
export function WbProgressPanel({
  accountId,
  onClose,
  onComplete
}) {
  const {
    request
  } = useApi();
  const [progress, setProgress] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    fetched: 0,
    pending: 0,
    errors: 0,
    totalRows: 0
  });
  const [prevStats, setPrevStats] = useState(null);
  const [startDate, setStartDate] = useState('2024-01-01');
  const logsEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const addLog = (message, type = 'info') => {
    const timestamp = fmtTime();
    setLogs(prev => [...prev, {
      timestamp,
      message,
      type
    }]);
  };
  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  // Загрузка текущего прогресса
  const loadProgress = async (silent = false) => {
    try {
      const data = await request(`/api/v1/tax-reports/wb/v2/${accountId}/progress`);
      setProgress(data);
      const newStats = {
        total: data.total,
        fetched: data.fetched,
        pending: data.pending,
        errors: data.error,
        totalRows: data.total_rows
      };

      // Логируем изменения
      if (prevStats && !silent) {
        if (newStats.fetched > prevStats.fetched) {
          const diff = newStats.fetched - prevStats.fetched;
          addLog(`📥 Загружено ещё ${diff} недель`, 'success');

          // Обновляем список отчётов в родительском компоненте
          if (onComplete) {
            onComplete();
          }
        }
        if (newStats.errors > prevStats.errors) {
          const diff = newStats.errors - prevStats.errors;
          addLog(`❌ Появилось ${diff} новых ошибок`, 'error');

          // Show error details from periods
          if (data.periods) {
            data.periods.forEach(p => {
              if (p.fetch_status === 'error' && p.error_message) {
                addLog(`  ${p.period}: ${p.error_message}`, 'error');
              }
            });
          }
        }
      }
      setStats(newStats);
      setPrevStats(newStats);
    } catch (error) {
      if (!silent) {
        addLog(`Ошибка загрузки прогресса: ${error.message}`, 'error');
      }
    }
  };

  // Остановка polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Автоматическая загрузка отчётов с polling
  const startFetch = async () => {
    setIsProcessing(true);
    setLogs([]);
    setPrevStats(null);
    addLog('🚀 Запуск фоновой загрузки WB отчётов...', 'info');
    addLog('⏱️  Загрузка может занять продолжительное время (WB API лимит: 1 запрос в минуту)', 'warning');
    try {
      // Запускаем фоновую задачу
      addLog(`📅 Начальная дата: ${startDate}`, 'info');
      const result = await request(`/api/v1/tax-reports/wb/v2/${accountId}/fetch-batch?start_date=${startDate}`, {
        method: 'POST'
      });
      addLog(`✅ ${result.message || result.status}`, 'success');
      addLog('📊 Отслеживание прогресса в реальном времени...', 'info');

      // Начинаем polling прогресса каждые 10 секунд
      await loadProgress();
      pollingIntervalRef.current = setInterval(async () => {
        await loadProgress();
      }, 10000); // 10 seconds
    } catch (error) {
      addLog(`❌ Критическая ошибка: ${error.message}`, 'error');
      setIsProcessing(false);
    }
  };

  // Остановка загрузки
  const stopFetch = () => {
    stopPolling();
    setIsProcessing(false);
    addLog('⏸️  Мониторинг прогресса остановлен', 'warning');
    addLog('ℹ️  Фоновая задача продолжает работать на сервере', 'info');
  };

  // Загрузка прогресса при монтировании и автостарт polling если есть pending
  useEffect(() => {
    const initProgress = async () => {
      try {
        const data = await request(`/api/v1/tax-reports/wb/v2/${accountId}/progress`);
        setProgress(data);
        const newStats = {
          total: data.total,
          fetched: data.fetched,
          pending: data.pending,
          errors: data.error,
          totalRows: data.total_rows
        };
        setStats(newStats);
        setPrevStats(newStats);

        // Если есть pending периоды - значит фоновая загрузка уже работает
        // Автоматически запускаем мониторинг
        if (data.pending > 0) {
          addLog('🔄 Обнаружена активная загрузка в фоне!', 'success');
          addLog(`📊 В процессе: ${data.pending} недель`, 'info');
          addLog('⏱️  Возобновление мониторинга прогресса...', 'info');
          setIsProcessing(true);

          // Запускаем polling
          pollingIntervalRef.current = setInterval(async () => {
            await loadProgress();
          }, 10000); // 10 seconds
        }
      } catch (error) {
        logger.error('Failed to init progress:', error);
      }
    };
    initProgress();
  }, [accountId]);

  // Очистка polling при размонтировании
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);
  return <div className="tax-progress-panel">
      <div className="tax-progress-header">
        <h2>Загрузка WB налоговых отчётов</h2>
        <button onClick={onClose} className="tax-progress-close">✕</button>
      </div>

      {/* Статистика */}
      {progress && <div className="tax-progress-stats">
          <div className="stat-item">
            <div className="stat-label">Всего недель</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Загружено</div>
            <div className="stat-value">{stats.fetched}</div>
          </div>
          <div className={`stat-item ${stats.pending > 0 ? 'not-available' : ''}`}>
            <div className="stat-label">В процессе</div>
            <div className="stat-value">{stats.pending}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Всего строк</div>
            <div className="stat-value">{stats.totalRows.toLocaleString()}</div>
          </div>
          <div className="stat-item error">
            <div className="stat-label">Ошибок</div>
            <div className="stat-value">{stats.errors}</div>
          </div>
        </div>}

      {/* Настройки загрузки */}
      {!isProcessing && <div className="fetch-settings">
          <div className="setting-group">
            <label htmlFor="start-date">Загрузить отчёты начиная с:</label>
            <input id="start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} max={new Date().toISOString().split('T')[0]} className="date-input" />
          
          </div>
          <div className="quick-dates">
            <button className="btn-quick" onClick={() => setStartDate('2025-01-01')}>
              2025
            </button>
            <button className="btn-quick" onClick={() => setStartDate('2024-01-01')}>
              2024
            </button>
            <button className="btn-quick" onClick={() => {
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          setStartDate(sixMonthsAgo.toISOString().split('T')[0]);
        }}>
              Последние 6 месяцев
            </button>
            <button className="btn-quick" onClick={() => {
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          setStartDate(oneYearAgo.toISOString().split('T')[0]);
        }}>
              Последний год
            </button>
          </div>
        </div>}

      {/* Кнопки управления */}
      <div className="tax-progress-controls">
        {!isProcessing ? <button onClick={startFetch} className="btn-primary">
          
            Запустить загрузку
          </button> : <button onClick={stopFetch} className="btn-warning">
          
            Остановить мониторинг
          </button>}
        <button onClick={() => loadProgress(false)} className="btn-secondary">
          
          Обновить прогресс
        </button>
      </div>

      {/* Окно логов */}
      <div className="tax-progress-logs">
        <div className="logs-header">
          Логи
          {isProcessing && <span className="status-indicator">● В процессе</span>}
        </div>
        <div className="logs-content">
          {logs.length === 0 ? <div className="logs-empty">
              {stats.pending > 0 ? '🔄 Фоновая загрузка обнаружена. Мониторинг активен.' : 'Нажмите "Запустить загрузку" для начала'}
            </div> : logs.map((log, index) => <div key={index} className={`log-entry log-${log.type}`}>
                <span className="log-timestamp">[{log.timestamp}]</span>
                <span className="log-message">{log.message}</span>
              </div>)}
          <div ref={logsEndRef} />
        </div>
      </div>
    </div>;
}
